import React from "react";
import { Box } from "@chakra-ui/layout";
import { SkeletonText, Link as ChakraLink } from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { Table, Tbody, Td, Th, Tr, Thead } from "@chakra-ui/table";
import { Link } from "react-router-dom";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { CopyText } from "../Utilities/CopyText";
import { PreviewButton } from "../Utilities/PreviewButton";
import { StatusBadge } from "../Utilities/StatusBadge";
import { CHERRY_PAY_CARD_STATUS_RULES } from "./cherryPayCardStatusRules";

interface CherryPayCardsTableProps {
  cards: CherryHubAdminApi.CherryPayCardCompact[] | null;
  isLoading: boolean;
  debouncedQuery: string;
}

export const CherryPayCardsTable = ({
  cards,
  isLoading,
  debouncedQuery,
}: CherryPayCardsTableProps) => {
  return (
    <Box w="100%" overflowX="auto">
      <Table size="sm">
        <Thead>
          <Tr>
            <Th whiteSpace="nowrap">Business</Th>
            <Th whiteSpace="nowrap">External Account ID</Th>
            <Th whiteSpace="nowrap">Mobile Number</Th>
            <Th whiteSpace="nowrap">Member</Th>
            <Th whiteSpace="nowrap">Status</Th>
            <Th whiteSpace="nowrap">Card Program Type</Th>
            <Th whiteSpace="nowrap">Company ID</Th>
            <Th whiteSpace="nowrap">Program ID</Th>
            <Th whiteSpace="nowrap">Created</Th>
          </Tr>
        </Thead>
        <Tbody>
          {!isLoading &&
            (cards ?? []).map((card) => (
              <Tr key={card.CardId}>
                <Td whiteSpace="nowrap">
                  {card.BusinessId ? (
                    <PreviewButton
                      url={`/admin/businesses/${card.BusinessId}`}
                      linkProps={{ target: "_self" }}
                      hover={true}
                    >
                      {card.BusinessId}
                    </PreviewButton>
                  ) : (
                    "-"
                  )}
                </Td>
                <Td width="15%" whiteSpace="nowrap" title="View card details.">
                  <CopyText
                    text={card.ExternalAccountId ?? ""}
                    copyOnIcon={true}
                    hover={true}
                  >
                    <ChakraLink
                      as={Link}
                      to={`/admin/cherrypay-cards/${card.CardId}`}
                    >
                      {card.ExternalAccountId ?? "-"}
                    </ChakraLink>
                  </CopyText>
                </Td>
                <Td whiteSpace="nowrap">
                  <CopyText
                    text={card.MobileNumber ?? ""}
                    copyOnIcon={true}
                    hover={true}
                  >
                    {card.MobileNumber ?? "-"}
                  </CopyText>
                </Td>
                <Td whiteSpace="nowrap">
                  {card.MemberId ? (
                    <ChakraLink
                      as={Link}
                      to={`/admin/members/${card.MemberId}`}
                      aria-label={`View member ${card.MemberId}`}
                      display="inline-flex"
                    >
                      <ExternalLinkIcon boxSize={4} />
                    </ChakraLink>
                  ) : null}
                </Td>
                <Td width="8%" whiteSpace="nowrap">
                  {card.AccountStatus != null &&
                  String(card.AccountStatus).trim() !== "" ? (
                    <StatusBadge
                      value={card.AccountStatus}
                      rules={CHERRY_PAY_CARD_STATUS_RULES}
                    />
                  ) : (
                    "-"
                  )}
                </Td>
                <Td whiteSpace="nowrap">{card.CardProgramType ?? "-"}</Td>
                <Td whiteSpace="nowrap">{card.CompanyId ?? "-"}</Td>
                <Td whiteSpace="nowrap">
                  {card.ProgramId != null ? String(card.ProgramId) : "-"}
                </Td>
                <Td whiteSpace="nowrap">
                  {card.CreatedDate
                    ? new Date(card.CreatedDate).toLocaleString()
                    : "-"}
                </Td>
              </Tr>
            ))}
          {cards?.length === 0 && !isLoading && (
            <Tr>
              <Td colSpan={9} textAlign="center">
                No CherryPay cards found
                {debouncedQuery ? ` matching '${debouncedQuery}'` : ""}
              </Td>
            </Tr>
          )}
          {isLoading && (
            <Tr>
              <Td width="15%">
                <SkeletonText noOfLines={12} spacing="4" width="100%" />
              </Td>
              <Td width="12%">
                <SkeletonText noOfLines={12} spacing="4" width="100%" />
              </Td>
              <Td width="12%">
                <SkeletonText noOfLines={12} spacing="4" width="100%" />
              </Td>
              <Td width="8%">
                <SkeletonText noOfLines={12} spacing="4" width="100%" />
              </Td>
              <Td width="12%">
                <SkeletonText noOfLines={12} spacing="4" width="100%" />
              </Td>
              <Td width="10%">
                <SkeletonText noOfLines={12} spacing="4" width="100%" />
              </Td>
              <Td width="8%">
                <SkeletonText noOfLines={12} spacing="4" width="100%" />
              </Td>
              <Td width="8%">
                <SkeletonText noOfLines={12} spacing="4" width="100%" />
              </Td>
              <Td width="8%">
                <SkeletonText noOfLines={12} spacing="4" width="100%" />
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </Box>
  );
};
